import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useAsyncFn } from 'react-use'
import { Context } from '../../../context'
import { trigger } from '../../../lib/events'
import { Summary } from '../../../types'
import { config } from '../../../config'
import { moisesSDK } from '../../../../../../packages/web-app-shell/src/lib/firebase'

interface UseEmbedPlayerProps {
  trackId?: string
  onDismissModal(): void
  summary: Summary
}

interface UseEmbedPlayer {
  loading: boolean

  isPublic: boolean
  onTogglePublic(): void

  valueEmbed?: string
  onGenerateEmbed(): void
}

const delay = async (stallTime = 3000): Promise<any> => {
  await new Promise((resolve) => setTimeout(resolve, stallTime))
}

export const useEmbedPlayer = ({
  trackId,
  onDismissModal,
  summary
}: UseEmbedPlayerProps): UseEmbedPlayer => {
  const {
    toast: { add: addToast }
  } = useContext(Context)
  const [loading, setLoading] = useState(false)

  const separate = useMemo(() => summary?.separate, [summary])

  const generateState = useCallback(() => {
    let data = localStorage.getItem('moises:track-settings') as any
    data = data ? JSON.parse(data) : []

    const minimalData = {
      trackId,
      operationType: separate?.legacy?.type || 'custom-music-stems',
      operationId: separate?.id
    }

    if (!data || !data.length) {
      return minimalData
    }

    const state = data
      .find((i: any) => i.id === trackId)
      ?.operations?.find((i: any) => i.type === minimalData.operationType)

    return {
      ...(state || {}),
      ...minimalData
    }
  }, [separate, trackId])

  const [stateGenerateEmbed, onGenerateEmbed] = useAsyncFn(async () => {
    setLoading(true)

    // trigger to update track state on storage
    trigger('player:channel-changed')
    await delay(1000)

    const state = generateState()

    const query = `
    mutation CreateEmbedded($data: EmbeddedInput!) {
      createEmbedded(data: $data)
    }`

    const result = await moisesSDK.graphQL.fetch({
      query,
      variables: {
        data: {
          trackId,
          state: JSON.stringify(state),
          settings: JSON.stringify({})
        }
      }
    })

    setLoading(false)

    return `<iframe src="https://audio${
      config.env === 'stage' ? '-stage' : ''
    }.moises.ai/embed/${
      result.createEmbedded
    }" width="100%" height="600" title="Moises Player" frameborder="0"></iframe>`
  }, [trackId, generateState])

  const [stateTogglePublic, onTogglePublic] = useAsyncFn(async () => {
    setLoading(true)
    const query = `
    mutation TogglePublic($trackId: String!) {
      togglePublic(trackId: $trackId)
    }`

    const result = await moisesSDK.graphQL.fetch({
      query,
      variables: {
        trackId
      }
    })

    if (result.togglePublic) {
      onGenerateEmbed()
    } else {
      setLoading(false)
    }

    return result.togglePublic
  }, [trackId, onGenerateEmbed])

  useEffect(() => {
    if (stateTogglePublic.error || stateGenerateEmbed.error) {
      onDismissModal()
      addToast({
        icon: null,
        type: 'error',
        closable: true,
        description: 'An error occurred when creating the embed. Try again.'
      })
    }
  }, [
    onDismissModal,
    addToast,
    stateTogglePublic?.error,
    stateGenerateEmbed?.error
  ])

  return {
    loading: loading || stateTogglePublic.loading || stateGenerateEmbed.loading,
    isPublic: stateTogglePublic.value,
    valueEmbed: stateGenerateEmbed.value,
    onTogglePublic,
    onGenerateEmbed
  }
}
